// Display formatting. A small closed set of named formats rather than an Excel
// format-string interpreter — the Pareto call. Intl does the heavy lifting, so
// thousands separators and currency symbols follow the user's locale.

import type { CellValue, FormatId } from '@/model/types';
import { formatSerial, isPlausibleSerial } from '@/model/dates';
import { dimensionOf } from '@/data/units';

export interface FormatOption {
  id: FormatId;
  label: string;
  sample: string;
}

export const FORMATS: FormatOption[] = [
  { id: 'general', label: 'General', sample: '1234.5' },
  { id: 'number0', label: 'Number', sample: '1,235' },
  { id: 'number1', label: 'Number (1dp)', sample: '1,234.5' },
  { id: 'number2', label: 'Number (2dp)', sample: '1,234.50' },
  { id: 'currency0', label: 'Currency', sample: '$1,235' },
  { id: 'currency2', label: 'Currency (2dp)', sample: '$1,234.50' },
  { id: 'percent0', label: 'Percent', sample: '12%' },
  { id: 'percent1', label: 'Percent (1dp)', sample: '12.3%' },
  { id: 'percent2', label: 'Percent (2dp)', sample: '12.35%' },
  { id: 'date', label: 'Date', sample: '01/03/2026' },
  { id: 'dateLong', label: 'Date (long)', sample: '1 Mar 2026' },
  { id: 'monthYear', label: 'Month', sample: 'Mar 2026' },
  { id: 'text', label: 'Text', sample: '1234.5' },
];

/**
 * The KIND of thing a format shows, ignoring how many decimals it shows it to.
 *
 * Typing `85%` into a cell says "this is a percentage" as plainly as choosing
 * the format from the menu does. But typing `85%` into a cell already showing
 * `12.5%` should not quietly drop it to `85%` — same family, so the format the
 * user picked stays and only the value changes.
 */
export function formatFamily(fmt: FormatId | undefined): 'number' | 'currency' | 'percent' | 'date' | 'text' | 'general' {
  if (fmt === undefined || fmt === 'general') return 'general';
  if (fmt.startsWith('currency')) return 'currency';
  if (fmt.startsWith('percent')) return 'percent';
  if (fmt.startsWith('number')) return 'number';
  if (fmt === 'text') return 'text';
  return 'date';
}

const cache = new Map<string, Intl.NumberFormat>();

function nf(options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = JSON.stringify(options);
  let f = cache.get(key);
  if (!f) {
    f = new Intl.NumberFormat(undefined, options);
    cache.set(key, f);
  }
  return f;
}

/** Currency symbol used by `currency*`. Set once from project settings. */
let currencyCode = 'USD';
export function setCurrency(code: string): void {
  if (code && code !== currencyCode) {
    currencyCode = code;
    cache.clear();
  }
}
export function getCurrency(): string {
  return currencyCode;
}

/**
 * `general` mimics Excel's default: integers plain, fractions trimmed to a
 * readable number of digits, very large/small magnitudes in exponent form.
 */
function formatGeneral(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  if (Number.isInteger(n) && Math.abs(n) < 1e15) return String(n);
  const abs = Math.abs(n);
  if (abs !== 0 && (abs >= 1e11 || abs < 1e-9)) return n.toExponential(5).replace(/e([+-])(\d)$/, 'e$10$2');
  return String(Number(n.toPrecision(10)));
}

export function formatValue(value: CellValue | undefined, fmt: FormatId | undefined): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
  if (typeof value === 'string') return value;
  if (!Number.isFinite(value)) return String(value);

  switch (fmt) {
    case 'number0':
      return nf({ maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(value);
    case 'number1':
      return nf({ maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(value);
    case 'number2':
      return nf({ maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(value);
    case 'currency0':
      return nf({
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }).format(value);
    case 'currency2':
      return nf({
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }).format(value);
    case 'percent0':
      return nf({ style: 'percent', maximumFractionDigits: 0 }).format(value);
    case 'percent1':
      return nf({ style: 'percent', maximumFractionDigits: 1 }).format(value);
    case 'percent2':
      return nf({ style: 'percent', maximumFractionDigits: 2 }).format(value);
    case 'date':
    case 'dateLong':
    case 'monthYear':
      // Dates are Excel serials, so YEAR/EOMONTH/TEXT work on them; the format
      // is the only thing that says "show this as a date".
      return formatSerial(value, fmt);
    case 'text':
      return String(value);
    case 'general':
    case undefined:
    default:
      return formatGeneral(value);
  }
}

/** Numbers and booleans sit right, text sits left — the spreadsheet convention. */
export function defaultAlign(value: CellValue | undefined): 'left' | 'right' | 'center' {
  if (typeof value === 'number') return 'right';
  if (typeof value === 'boolean') return 'center';
  return 'left';
}

/** A sensible format for a Giraffe measure, given its field name. */
export function isDateFormat(fmt: FormatId | undefined): boolean {
  return fmt === 'date' || fmt === 'dateLong' || fmt === 'monthYear';
}

/** A date cell is a serial number wearing a date format — both halves matter. */
export function isDateValue(value: CellValue | undefined, fmt: FormatId | undefined): boolean {
  return isDateFormat(fmt) && typeof value === 'number' && isPlausibleSerial(value);
}

export function formatForField(field: string): FormatId {
  const f = field.toLowerCase();
  if (/date$/.test(f) || /^date/.test(f)) return 'date';
  if (/price|cost|revenue|value|rent|\$/.test(f)) return 'currency0';
  if (/efficiency|ratio|percent|pct/.test(f)) return 'percent0';
  if (/count|units|dwelling|levels|storeys|stories/.test(f)) return 'number0';
  if (/area|perimeter/.test(f)) return 'number0';
  /*
   * A length keeps a decimal, because converting one rarely leaves a round
   * number: 3.2 m is 10.498687664 ft, and `general` prints every digit of it.
   * One decimal is what a floor-to-floor is quoted to in either system.
   */
  if (dimensionOf(field) === 'length') return 'number1';
  return 'general';
}
